import { createApi } from '@reduxjs/toolkit/query/react';

import { axiosBaseQuery } from '@/api/api';
import { ENDPOINT } from '@/constants/endpoints';
import { UUID } from '@/types/common';
import {
  ManagementAllQuestionItem,
  ManagementCategory,
  QuestionCategory,
  TranslationCategory,
  TranslationQuestion,
} from '@/types/management/managementCategories';

type CreateManagementCategoryPayload = {
  icon: string;
  data: TranslationCategory[];
};

type UpdateManagementCategoryPayload = {
  id: UUID;
  body: {
    icon?: string;
    data?: TranslationCategory[];
  };
};

type TranslateManagementCategoryPayload = {
  name: string;
  description: string;
};

type CreateManagementQuestionPayload = {
  id: UUID;
  body: {
    data: TranslationQuestion[];
  };
};

type UpdateManagementQuestionPayload = {
  id: UUID;
  body: {
    data: TranslationQuestion[];
  };
};

type TranslateManagementQuestionPayload = {
  text: string;
};

type GetManagementAllQuestionsPayload = {
  search: string;
};

export const managementCategoriesApi = createApi({
  reducerPath: 'managementCategoriesApi',
  baseQuery: axiosBaseQuery,
  tagTypes: ['ManagementCategories', 'ManagementQuestions'],
  endpoints: builder => ({
    getManagementCategories: builder.query<ManagementCategory[], void>({
      query: () => ({
        url: ENDPOINT.MANAGEMENT.CATEGORIES.GET_ALL(),
        method: 'GET',
      }),
      providesTags: ['ManagementCategories'],
    }),
    getManagementCategoryTranslation: builder.query<TranslationCategory[], UUID>({
      query: id => ({
        url: ENDPOINT.MANAGEMENT.CATEGORIES.GET_TRANSLATION_CATEGORY(id),
        method: 'GET',
      }),
    }),
    createManagementCategory: builder.mutation<void, CreateManagementCategoryPayload>({
      query: body => ({
        url: ENDPOINT.MANAGEMENT.CATEGORIES.CREATE(),
        method: 'POST',
        body,
      }),
      invalidatesTags: ['ManagementCategories'],
    }),
    updateManagementCategory: builder.mutation<void, UpdateManagementCategoryPayload>({
      query: ({ id, body }) => ({
        url: ENDPOINT.MANAGEMENT.CATEGORIES.UPDATE(id),
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['ManagementCategories'],
    }),
    deleteManagementCategory: builder.mutation<void, UUID>({
      query: id => ({
        url: ENDPOINT.MANAGEMENT.CATEGORIES.DELETE(id),
        method: 'DELETE',
      }),
      invalidatesTags: ['ManagementCategories'],
    }),
    translateManagementCategory: builder.mutation<
      TranslationCategory[],
      TranslateManagementCategoryPayload
    >({
      query: body => ({
        url: ENDPOINT.MANAGEMENT.CATEGORIES.TRANSLATE_CATEGORY(),
        method: 'POST',
        body,
      }),
    }),

    getManagementQuestions: builder.query<QuestionCategory, UUID>({
      query: id => ({
        url: ENDPOINT.MANAGEMENT.QUESTIONS.GET_QUESTIONS(id),
        method: 'GET',
      }),
      providesTags: ['ManagementQuestions'],
    }),
    getManagementAllQuestions: builder.query<
      ManagementAllQuestionItem[],
      GetManagementAllQuestionsPayload
    >({
      query: params => ({
        url: ENDPOINT.MANAGEMENT.QUESTIONS.GET_ALL(),
        method: 'GET',
        params,
      }),
    }),
    getManagementQuestionTranslation: builder.query<TranslationQuestion[], UUID>({
      query: id => ({
        url: ENDPOINT.MANAGEMENT.QUESTIONS.GET_TRANSLATION(id),
        method: 'GET',
      }),
    }),
    createManagementQuestion: builder.mutation<void, CreateManagementQuestionPayload>({
      query: ({ id, body }) => ({
        url: ENDPOINT.MANAGEMENT.QUESTIONS.CREATE(id),
        method: 'POST',
        body,
      }),
      invalidatesTags: ['ManagementQuestions', 'ManagementCategories'],
    }),
    updateManagementQuestion: builder.mutation<void, UpdateManagementQuestionPayload>({
      query: ({ id, body }) => ({
        url: ENDPOINT.MANAGEMENT.QUESTIONS.UPDATE(id),
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['ManagementQuestions', 'ManagementCategories'],
    }),
    deleteManagementQuestion: builder.mutation<void, UUID>({
      query: id => ({
        url: ENDPOINT.MANAGEMENT.QUESTIONS.DELETE(id),
        method: 'DELETE',
      }),
      invalidatesTags: ['ManagementQuestions', 'ManagementCategories'],
    }),
    translateManagementQuestion: builder.mutation<
      TranslationQuestion[],
      TranslateManagementQuestionPayload
    >({
      query: body => ({
        url: ENDPOINT.MANAGEMENT.QUESTIONS.TRANSLATE(),
        method: 'POST',
        body,
      }),
    }),
  }),
});

export const {
  useGetManagementCategoriesQuery,
  useGetManagementCategoryTranslationQuery,
  useCreateManagementCategoryMutation,
  useUpdateManagementCategoryMutation,
  useDeleteManagementCategoryMutation,
  useTranslateManagementCategoryMutation,
  useCreateManagementQuestionMutation,
  useDeleteManagementQuestionMutation,
  useGetManagementAllQuestionsQuery,
  useGetManagementQuestionTranslationQuery,
  useGetManagementQuestionsQuery,
  useTranslateManagementQuestionMutation,
  useUpdateManagementQuestionMutation,
} = managementCategoriesApi;
